import React, { useState, useEffect, useCallback } from 'react';
import Pagination from './Pagination';
import './BetLogs.css';

const API_BASE = '/api';

function BetLogs() {
  const [goals, setGoals] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filterOptions, setFilterOptions] = useState({ playErrorCodes: [], validateErrorCodes: [], errorCodes: [], skipReasons: [], players: [], accounts: [] });
  const [displayNameMap, setDisplayNameMap] = useState({});

  // Default dates: current month 1st → today
  const getDefaultDates = () => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth()+1).padStart(2,'0'), d = String(now.getDate()).padStart(2,'0');
    return { start: `${y}-${m}-01`, end: `${y}-${m}-${d}` };
  };
  const defaults = getDefaultDates();

  // Filters
  const [filterResult, setFilterResult] = useState('');
  const [filterCache, setFilterCache] = useState('');
  const [filterMarketCheck, setFilterMarketCheck] = useState('');
  const [filterHesap, setFilterHesap] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterPlayError, setFilterPlayError] = useState('');
  const [filterValidateError, setFilterValidateError] = useState('');
  const [filterGoalTeam, setFilterGoalTeam] = useState('');
  const [filterPlayer, setFilterPlayer] = useState('');
  const [filterPlayer2, setFilterPlayer2] = useState('');
  const [filterSov, setFilterSov] = useState('');
  const [filterBarcode, setFilterBarcode] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(defaults.start);
  const [filterEndDate, setFilterEndDate] = useState(defaults.end);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const token = localStorage.getItem('token');

  // Fetch filter options and displayName map on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/goals/filter-options`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setFilterOptions(data.data);
      } catch (err) { console.error('Filter options error:', err); }
      try {
        const res = await fetch(`${API_BASE}/nesine-accounts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) {
          const dnMap = {};
          data.data.forEach(a => { if (a.displayName) dnMap[a.username] = a.displayName; });
          setDisplayNameMap(dnMap);
        }
      } catch {}
    })();
  }, [token]);

  const fetchGoals = useCallback(async (page = 1, useFilters = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 50);
      if (useFilters) {
        if (filterResult !== '') params.set('result', filterResult);
        if (filterCache !== '') params.set('cache', filterCache);
        if (filterMarketCheck !== '') params.set('marketCheck', filterMarketCheck);
        if (filterHesap !== '') params.set('hesap', filterHesap);
        if (filterAccount !== '') params.set('account', filterAccount);
        if (filterPlayError !== '') params.set('playErrorCode', filterPlayError);
        if (filterValidateError !== '') params.set('validateErrorCode', filterValidateError);
        if (filterGoalTeam !== '') params.set('goalTeam', filterGoalTeam);
        if (filterPlayer.trim()) params.set('player', filterPlayer.trim());
        if (filterPlayer2.trim()) params.set('player2', filterPlayer2.trim());
        if (filterSov.trim()) params.set('sov', filterSov.trim());
        if (filterBarcode !== '') params.set('barcode', filterBarcode);
        if (filterStartDate) params.set('startDate', filterStartDate);
        if (filterEndDate) params.set('endDate', filterEndDate);
      }

      const res = await fetch(`${API_BASE}/goals/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setGoals(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Goals fetch error:', err);
    }
    setLoading(false);
  }, [token, filterResult, filterCache, filterMarketCheck, filterHesap, filterAccount, filterPlayError, filterValidateError, filterGoalTeam, filterPlayer, filterPlayer2, filterSov, filterBarcode, filterStartDate, filterEndDate]);

  // Fetch on mount — no filters
  useEffect(() => {
    fetchGoals(1, false);
  }, []); // eslint-disable-line

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchGoals(newPage, filtersApplied);
    setExpandedRows(new Set());
  };

  const handleFilter = () => {
    setFiltersApplied(true);
    fetchGoals(1, true);
    setExpandedRows(new Set());
  };

  const handleClearFilters = () => {
    setFilterResult('');
    setFilterCache('');
    setFilterMarketCheck('');
    setFilterHesap('');
    setFilterAccount('');
    setFilterPlayError('');
    setFilterValidateError('');
    setFilterGoalTeam('');
    setFilterPlayer('');
    setFilterPlayer2('');
    setFilterSov('');
    setFilterBarcode('');
    setFilterStartDate(defaults.start);
    setFilterEndDate(defaults.end);
    setFiltersApplied(false);
    fetchGoals(1, false);
    setExpandedRows(new Set());
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    const date = new Date(isoStr);
    return date.toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const parseTeamName = (fullName) => {
    const match = fullName?.match(/^(.+?)\s*\(([^)]+)\)$/);
    if (match) {
      return { club: match[1].trim(), player: match[2].trim() };
    }
    return { club: '', player: fullName || '?' };
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Normalize goal: old flat format → accounts array for uniform rendering
  const normalizeGoal = (goal) => {
    if (goal.accounts && goal.accounts.length > 0) return goal;
    // Old format: wrap flat fields into virtual accounts array
    if (goal.nesineUser && (goal.betAttempted || goal.betSuccess != null)) {
      return {
        ...goal,
        accounts: [{
          nesineUser: goal.nesineUser,
          role: goal.role,
          betAttempted: goal.betAttempted,
          betSuccess: goal.betSuccess,
          betError: goal.betError,
          barcode: goal.barcode,
          odd: goal.odd,
          amount: goal.amount,
          potentialWin: goal.potentialWin,
          sov: goal.sov,
          outcomeN: goal.outcomeN,
          outcomeName: goal.outcomeName,
          playErrorCode: goal.playErrorCode,
          validateErrorCode: goal.validateErrorCode,
          validateSuccess: goal.validateSuccess,
          playRequest: goal.playRequest,
          playResponse: goal.playResponse,
          validateRequest: goal.validateRequest,
          validateResponse: goal.validateResponse,
          probe: goal.probe,
          batches: goal.batches,
        }],
        // Normalize market check from old flat fields
        marketCheck: goal.marketCheck || (goal.marketCheckResult ? {
          result: goal.marketCheckResult,
          request: goal.marketCheckRequest,
          response: goal.marketCheckResponse,
        } : null),
      };
    }
    return { ...goal, accounts: goal.accounts || [] };
  };

  // Row color logic
  const getRowClass = (goal) => {
    if (goal.autoBetEnabled === false) return 'bl-row-autobet-off';
    if (goal.betSkipReason === 'notLoggedIn' || goal.betSkipReason === 'sessionExpired_401') return 'bl-row-nologin';
    if (goal.betSkipReason === 'marketCheckClosed') return 'bl-row-market-kontrol';
    const hasInitialFailed = goal.accounts?.some(a => a.betSkipReason === 'initialFailed');
    if (hasInitialFailed && !goal.betSuccess) return 'bl-row-fail';
    if (goal.betSuccess === true) return 'bl-row-success';
    if (goal.betAttempted === true && goal.betSuccess === false) return 'bl-row-fail';
    return '';
  };

  // Market check display helper
  const getMarketCheckDisplay = (goal) => {
    const mc = goal.marketCheck;
    const mcResult = mc?.result || goal.marketCheckResult || goal.acikMarketResult;
    if (mcResult === 'open') return { text: '✅ Açık', cls: 'bl-acik-open' };
    if (mcResult === 'closed') return { text: '🔒 Kapalı', cls: 'bl-acik-closed' };
    if (mcResult) {
      const label = mcResult.startsWith('delayCache') ? 'Gecikmeli: ' + mcResult.replace('delayCache', '')
        : mcResult.startsWith('cache') ? 'Anlık: ' + mcResult.replace('cache', '')
        : mcResult.startsWith('http') ? 'HTTP: ' + mcResult.replace('http', '')
        : mcResult;
      return { text: `🔒 ${label}`, cls: 'bl-acik-closed' };
    }
    return null;
  };

  return (
    <div className="bet-logs">
      {/* Header */}
      <div className="bl-header">
        <div className="bl-header-left">
          <h2>🎫 Kupon Logları</h2>
          {pagination.total > 0 && (
            <span className="bl-count">{pagination.total} kayıt</span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bl-filters">
        {/* Row 1: Akış adımları */}
        <div className="bl-filter-row">
          <div className="filter-group">
            <label>Sonuç</label>
            <select value={filterResult} onChange={e => setFilterResult(e.target.value)}>
              <option value="">Tümü</option>
              <option value="success">✅ Başarılı</option>
              <option value="fail">❌ Başarısız</option>
              <option value="skipped">⏭️ Atlandı</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Anlık Cache</label>
            <select value={filterCache} onChange={e => setFilterCache(e.target.value)}>
              <option value="">Tümü</option>
              <option value="passed">✅ Geçti</option>
              <option value="noEvent">❌ Event Yok</option>
              <option value="noMarket">❌ Market Yok</option>
              <option value="noOutcome">❌ Outcome Yok</option>
              <option value="oddIs1">⚠️ Oran 1.0</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Market Kontrol</label>
            <select value={filterMarketCheck} onChange={e => setFilterMarketCheck(e.target.value)}>
              <option value="">Tümü</option>
              <option value="open">✅ Açık</option>
              <option value="closed">🔒 Kapalı</option>
              <option value="skipped">— Yapılmadı</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Hesap Durumu</label>
            <select value={filterHesap} onChange={e => setFilterHesap(e.target.value)}>
              <option value="">Tümü</option>
              <option value="ok">✅ Hesap Hazır</option>
              <option value="notLoggedIn">🔒 Giriş Yok</option>
              <option value="autoBetDisabled">⏸️ Oto Kupon Kapalı</option>
              <option value="sessionExpired_401">🔒 Oturum Doldu</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Hesap</label>
            <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
              <option value="">Tümü</option>
              {filterOptions.accounts.map(a => (
                <option key={a.username} value={a.username}>{a.displayName || a.username}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Play Hata</label>
            <select value={filterPlayError} onChange={e => setFilterPlayError(e.target.value)}>
              <option value="">Tümü</option>
              <option value="none">Hata Yok</option>
              {(filterOptions.playErrorCodes || filterOptions.errorCodes || []).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="other">Diğer</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Validate Hata</label>
            <select value={filterValidateError} onChange={e => setFilterValidateError(e.target.value)}>
              <option value="">Tümü</option>
              <option value="none">Hata Yok</option>
              {(filterOptions.validateErrorCodes || []).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="other">Diğer</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Barkod</label>
            <select value={filterBarcode} onChange={e => setFilterBarcode(e.target.value)}>
              <option value="">Tümü</option>
              <option value="yes">✅ Var</option>
              <option value="no">❌ Yok</option>
            </select>
          </div>
        </div>

        {/* Row 2: Arama ve detay */}
        <div className="bl-filter-row">
          <div className="filter-group">
            <label>Oyuncu 1</label>
            <input type="text" placeholder="ör: Vito, Felix..." value={filterPlayer} onChange={e => setFilterPlayer(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleFilter()} />
          </div>
          <div className="filter-group">
            <label>Oyuncu 2</label>
            <input type="text" placeholder="ör: Splinter, Jack..." value={filterPlayer2} onChange={e => setFilterPlayer2(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleFilter()} />
          </div>
          <div className="filter-group">
            <label>Gol Atan</label>
            <select value={filterGoalTeam} onChange={e => setFilterGoalTeam(e.target.value)}>
              <option value="">Tümü</option>
              <option value="home">🏠 Ev Sahibi</option>
              <option value="away">✈️ Deplasman</option>
            </select>
          </div>
          <div className="filter-group">
            <label>T.Gol</label>
            <input type="number" placeholder="ör: 3" min="1" max="20" value={filterSov} onChange={e => setFilterSov(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleFilter()} />
          </div>
          <div className="filter-group">
            <label>Başlangıç</label>
            <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Bitiş</label>
            <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} />
          </div>
          <div className="filter-actions">
            <button className="filter-btn" onClick={handleFilter}>🔍 Filtrele</button>
            <button className="filter-clear-btn" onClick={handleClearFilters}>🗑️ Temizle</button>
          </div>
        </div>
      </div>

      {/* Top Pagination */}
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={(p) => { handlePageChange(p); }}
      />

      {/* Table */}
      <div className="bl-table-container">
        {loading ? (
          <div className="bl-loading">
            <div className="spinner" />
            <p>Yükleniyor...</p>
          </div>
        ) : goals.length === 0 ? (
          <div className="bl-empty">
            <span className="bl-empty-icon">📭</span>
            <p>Henüz kupon logu yok.</p>
          </div>
        ) : (
          <table className="bl-table">
            <thead>
              <tr>
                <th className="bl-th-time">Zaman</th>
                <th className="bl-th-home">Ev</th>
                <th className="bl-th-away">Dep</th>
                <th className="bl-th-score">Skor</th>
                <th className="bl-th-total">T.Gol</th>
                <th className="bl-th-market">Market</th>
                <th className="bl-th-account">Hesap</th>
                <th className="bl-th-iplay">I.Play</th>
                <th className="bl-th-ival">I.Val</th>
                <th className="bl-th-pplay">P.Play</th>
                <th className="bl-th-pval">P.Val</th>
                <th className="bl-th-barcode">Barkod</th>
              </tr>
            </thead>
            <tbody>
              {goals.map((rawGoal) => {
                const goal = normalizeGoal(rawGoal);
                const isExpanded = expandedRows.has(goal._id);
                const rowClass = getRowClass(goal);
                const mcDisplay = getMarketCheckDisplay(goal);
                const initialAcc = goal.accounts.find(a => a.role === 'initial') || (goal.accounts.length === 1 ? goal.accounts[0] : null);
                const coInitialAccs = goal.accounts.filter(a => a.role === 'co-initial');
                const initialGroupAccs = goal.accounts.filter(a => a.role === 'initial' || a.role === 'co-initial');
                const parallelAccs = goal.accounts.filter(a => a !== initialAcc && a.role !== 'co-initial');
                const hasDetail = goal.accounts.length > 0 || goal.marketCheck || goal.betSkipReason || goal.marketCheckRequest || goal.acikMarketRequest;
                return (
                  <React.Fragment key={goal._id}>
                    <tr
                      className={`bl-row ${rowClass} ${hasDetail ? 'bl-row-clickable' : ''}`}
                      onClick={() => hasDetail && toggleRow(goal._id)}
                    >
                      <td className="bl-td-time">{formatTime(goal.createdAt)}</td>
                      <td className={`bl-td-team ${goal.goalTeam === 'home' ? 'bl-td-scorer' : ''}`}>
                        {(() => { const t = parseTeamName(goal.homeTeam); return (
                          <div className="bl-team-cell">
                            <span className="bl-team-player">{t.player}</span>
                            {t.club && <span className="bl-team-club">{t.club}</span>}
                          </div>
                        ); })()}
                      </td>
                      <td className={`bl-td-team ${goal.goalTeam === 'away' ? 'bl-td-scorer' : ''}`}>
                        {(() => { const t = parseTeamName(goal.awayTeam); return (
                          <div className="bl-team-cell">
                            <span className="bl-team-player">{t.player}</span>
                            {t.club && <span className="bl-team-club">{t.club}</span>}
                          </div>
                        ); })()}
                      </td>
                      <td className="bl-td-score">
                        <span className="bl-score-display">
                          <span className={goal.goalTeam === 'home' ? 'bl-score-hl' : ''}>{goal.homeScore}</span>
                          <span className="bl-score-sep">-</span>
                          <span className={goal.goalTeam === 'away' ? 'bl-score-hl' : ''}>{goal.awayScore}</span>
                        </span>
                      </td>
                      <td className="bl-td-total">
                        <span className="bl-total-badge">{goal.totalGoals}</span>
                      </td>
                      {/* Market */}
                      <td className="bl-td-market">
                        {(() => {
                          if (goal.betSkipReason === 'noEvent') return <span className="market-badge market-fail" title="Event bulunamadı">❌ Anlık Cache</span>;
                          if (goal.betSkipReason === 'noMarket') return <span className="market-badge market-fail" title="Market bulunamadı">❌ Anlık Cache</span>;
                          if (goal.betSkipReason === 'noOutcome') return <span className="market-badge market-fail" title="Outcome bulunamadı">❌ Anlık Cache</span>;
                          if (goal.betSkipReason === 'oddIs1') return <span className="market-badge market-warn">⚠️ 1.0</span>;
                          if (goal.betSkipReason === 'marketCheckClosed') {
                            if (mcDisplay) return <span className={`market-badge ${mcDisplay.cls === 'bl-acik-open' ? 'market-open' : 'market-fail'}`}>{mcDisplay.text}</span>;
                            return <span className="market-badge market-fail">🔒 Kapalı</span>;
                          }
                          if (mcDisplay) return (
                            <>
                              <span className={`market-badge ${mcDisplay.cls === 'bl-acik-open' ? 'market-open' : 'market-fail'}`}>{mcDisplay.text}</span>
                              {goal.marketCheck?.suresi && <span className="bl-market-suresi">{goal.marketCheck.suresi}ms</span>}
                            </>
                          );
                          if (goal.autoBetEnabled === false || goal.betSkipReason === 'notLoggedIn' || goal.betSkipReason === 'sessionExpired_401' || goal.betSkipReason === 'autoBetDisabled') return <span className="market-badge market-ok">✅ Cache</span>;
                          if (goal.betAttempted || goal.betSuccess) return <span className="market-badge market-ok">✅ Cache</span>;
                          return <span className="market-none">—</span>;
                        })()}
                      </td>
                      {/* Hesap */}
                      <td className="bl-td-account">
                        {goal.accounts.length > 0 ? (
                          goal.accounts
                            .sort((a, b) => {
                              const roleOrder = { initial: 0, 'co-initial': 1, follower: 2, parallel: 3 };
                              return (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9);
                            })
                            .map((a, i) => (
                              <span key={i} className={`bl-acc-tag${a.role === 'initial' ? ' bl-acc-initial' : a.role === 'co-initial' ? ' bl-acc-co-initial' : ''}`}>
                                {a.displayName || displayNameMap[a.nesineUser] || a.nesineUser}
                              </span>
                            ))
                        ) : goal.initialAccount ? (
                          <span className="bl-acc-tag bl-acc-initial">{goal.initialAccount.displayName || goal.initialAccount.username}</span>
                        ) : '—'}
                      </td>
                      {/* I.Play */}
                      <td className="bl-td-iplay">
                        {(() => {
                          if (initialGroupAccs.length === 0) return '—';
                          return initialGroupAccs.map((acc, i) => {
                            if (acc.betSkipReason) return <div key={i} className="pv-line"><span className="pv-badge pv-skip">⏭️</span></div>;
                            if (acc.playErrorCode) return <div key={i} className="pv-line"><span className={`pv-errcode ${acc.betSuccess ? 'pv-errcode-resolved' : 'pv-errcode-fail'}`}>{acc.playErrorCode}</span></div>;
                            if (acc.playResponse || acc.batches?.length) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                            if (acc.betError) return <div key={i} className="pv-line"><span className="pv-badge pv-fail">❌</span></div>;
                            return <div key={i} className="pv-line">—</div>;
                          });
                        })()}
                      </td>
                      {/* I.Val */}
                      <td className="bl-td-ival">
                        {(() => {
                          if (initialGroupAccs.length === 0) return '—';
                          return initialGroupAccs.map((acc, i) => {
                            if (acc.betSkipReason) return <div key={i} className="pv-line">—</div>;
                            if (acc.validateSuccess === true) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                            if (acc.validateSuccess === false) return <div key={i} className="pv-line"><span className="pv-errcode pv-errcode-fail">{acc.validateErrorCode || '❌'}</span></div>;
                            return <div key={i} className="pv-line">—</div>;
                          });
                        })()}
                      </td>
                      {/* P.Play */}
                      <td className="bl-td-pplay">
                        {(() => {
                          if (parallelAccs.length === 0) return '—';
                          return parallelAccs.map((acc, i) => {
                            if (acc.betSkipReason) return <div key={i} className="pv-line"><span className="pv-badge pv-skip">⏭️</span></div>;
                            if (acc.playErrorCode) return <div key={i} className="pv-line"><span className={`pv-errcode ${acc.betSuccess ? 'pv-errcode-resolved' : 'pv-errcode-fail'}`}>{acc.playErrorCode}</span></div>;
                            if (acc.playResponse || acc.batches?.length) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                            if (acc.betError) return <div key={i} className="pv-line"><span className="pv-badge pv-fail">❌</span></div>;
                            return <div key={i} className="pv-line">—</div>;
                          });
                        })()}
                      </td>
                      {/* P.Val */}
                      <td className="bl-td-pval">
                        {(() => {
                          if (parallelAccs.length === 0) return '—';
                          return parallelAccs.map((acc, i) => {
                            if (acc.betSkipReason) return <div key={i} className="pv-line">—</div>;
                            if (acc.validateSuccess === true) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                            if (acc.validateSuccess === false) return <div key={i} className="pv-line"><span className="pv-errcode pv-errcode-fail">{acc.validateErrorCode || '❌'}</span></div>;
                            return <div key={i} className="pv-line">—</div>;
                          });
                        })()}
                      </td>
                      {/* Barkod */}
                      <td className="bl-td-barcode">
                        {(() => {
                          const allAccs = [...goal.accounts].sort((a, b) => {
                            const roleOrder = { initial: 0, 'co-initial': 1, follower: 2, parallel: 3 };
                            return (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9);
                          }).filter(a => a.barcode);
                          if (allAccs.length > 0) {
                            return allAccs.map((a, i) => <div key={i} className="bl-barcode" title={a.displayName || displayNameMap[a.nesineUser] || a.nesineUser}>{a.barcode}</div>);
                          }
                          return '—';
                        })()}
                      </td>
                    </tr>
                    {hasDetail && isExpanded && (
                      <tr className="bl-detail-row">
                        <td colSpan="12">
                          <div className={`bl-detail-panel ${goal.betSkipReason === 'marketCheckClosed' ? 'bl-panel-market-kontrol' : goal.betSuccess ? 'bl-panel-success' : goal.betAttempted ? 'bl-panel-fail' : 'bl-panel-info'}`}>

                            {/* Adım 1: Anlık Cache */}
                            <div className="flow-step">
                              <div className="flow-step-header">
                                <span className="flow-step-num">1</span>
                                <span className="flow-step-title">Anlık Cache</span>
                                {goal.betSkipReason === 'noEvent' ? <span className="flow-badge flow-badge-fail">❌ Event bulunamadı</span>
                                : goal.betSkipReason === 'noMarket' ? <span className="flow-badge flow-badge-fail">❌ Market bulunamadı</span>
                                : goal.betSkipReason === 'noOutcome' ? <span className="flow-badge flow-badge-fail">❌ Outcome bulunamadı</span>
                                : goal.betSkipReason === 'oddIs1' ? <span className="flow-badge flow-badge-warn">⚠️ Oran 1.0</span>
                                : <span className="flow-badge flow-badge-ok">✅ Event + Market + Outcome bulundu</span>}
                                <span className="mk-params">EV:{goal.matchCode} MTID:634 SOV:{goal.totalGoals} N:{goal.goalTeam === 'home' ? 1 : 3}{goal.nextGoalMarket ? ` MV:${goal.nextGoalMarket.mv} MID:${goal.nextGoalMarket.marketId}` : ''}</span>
                              </div>
                            </div>

                            {/* Adım 2: Market Kontrol */}
                            {(goal.marketCheck || goal.marketCheckRequest || goal.acikMarketRequest) && (() => {
                              const mc = goal.marketCheck || {};
                              const mcReq = mc.request || goal.marketCheckRequest || goal.acikMarketRequest;
                              const mcRes = mc.response || goal.marketCheckResponse || goal.acikMarketResponse;
                              const mcResult = mc.result || goal.marketCheckResult || goal.acikMarketResult;
                              return (
                                <div className="flow-step">
                                  <div className="flow-step-header">
                                    <span className="flow-step-num">2</span>
                                    <span className="flow-step-title">Market Kontrol{mc.suresi ? ` (${mc.suresi}ms)` : ''}</span>
                                    {mcResult === 'open' ? <span className="flow-badge flow-badge-ok">✅ Açık</span>
                                    : mcResult?.startsWith('cache') ? <span className="flow-badge flow-badge-fail">❌ Anlık Cache: {mcResult.replace('cache', '')}</span>
                                    : mcResult?.startsWith('delayCache') ? <span className="flow-badge flow-badge-fail">❌ Gecikmeli Cache: {mcResult.replace('delayCache', '')}</span>
                                    : mcResult?.startsWith('http') ? <span className="flow-badge flow-badge-fail">❌ HTTP: {mcResult.replace('http', '')}</span>
                                    : <span className="flow-badge flow-badge-fail">❌ {mcResult || 'Kapalı'}</span>}
                                    <span className="mk-params">EV:{goal.matchCode} MTID:634 SOV:{goal.totalGoals} N:{goal.goalTeam === 'home' ? 1 : 3} MV:{goal.nextGoalMarket?.mv || goal.marketCheck?.mv || '?'} MID:{goal.nextGoalMarket?.marketId || goal.marketCheck?.marketId || '?'}</span>
                                  </div>
                                  {mcReq && (
                                    <div className="flow-step-body">
                                      <div className="bl-section"><h4>Request</h4><pre>{JSON.stringify(mcReq, null, 2)}</pre></div>
                                      <div className="bl-section"><h4>Response</h4><pre>{JSON.stringify(mcRes, null, 2)}</pre></div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Hesap Durumu (hesap yoksa / giriş yapılmamışsa) */}
                            {goal.accounts.length === 0 && !['noEvent','noMarket','noOutcome','oddIs1','marketCheckClosed'].includes(goal.betSkipReason) && (
                              <div className="flow-step">
                                <div className="flow-step-header">
                                  <span className="flow-step-num">{(goal.marketCheck || goal.marketCheckRequest || goal.acikMarketRequest) ? '3' : '2'}</span>
                                  <span className="flow-step-title">Hesap Durumu</span>
                                  {goal.betSkipReason === 'notLoggedIn' ? <span className="flow-badge flow-badge-warn">🔒 Giriş Yapılmamış</span>
                                  : goal.betSkipReason === 'autoBetDisabled' || goal.autoBetEnabled === false ? <span className="flow-badge flow-badge-warn">⏸️ Oto Kupon Kapalı</span>
                                  : goal.betSkipReason === 'sessionExpired_401' ? <span className="flow-badge flow-badge-warn">🔒 Oturum Doldu (401)</span>
                                  : <span className="flow-badge flow-badge-fail">❌ {goal.betSkipReason || 'Bilinmeyen'}</span>}
                                </div>
                              </div>
                            )}

                            {/* Initial Grup (initial + co-initials) */}
                            {initialGroupAccs.length > 0 && (
                              <div className="flow-step">
                                <div className="flow-step-header">
                                  <span className="flow-step-num">{(goal.marketCheck || goal.marketCheckRequest || goal.acikMarketRequest) ? '3' : '2'}</span>
                                  <span className="flow-step-title">🎯 Initial Grup ({initialGroupAccs.length})</span>
                                  {(() => {
                                    const played = initialGroupAccs.filter(a => !a.betSkipReason);
                                    const ok = played.filter(a => a.betSuccess).length;
                                    if (played.length === 0) return <span className="flow-badge flow-badge-skip">⏭️ Tümü atlandı</span>;
                                    return ok > 0
                                      ? <span className="flow-badge flow-badge-ok">✅ {ok}/{played.length}</span>
                                      : <span className="flow-badge flow-badge-fail">❌ 0/{played.length}</span>;
                                  })()}
                                </div>
                                {initialGroupAccs.map((acc, ai) => (
                                  <div key={ai} className="flow-sub-account">
                                    <div className="flow-sub-header">
                                      <strong>{acc.role === 'initial' ? '🎯 ' : '🔗 '}{acc.displayName || displayNameMap[acc.nesineUser] || acc.nesineUser}</strong>
                                      <span className="flow-role-tag">{acc.role === 'initial' ? 'initial' : 'co-initial'}</span>
                                      {acc.betSkipReason ? <span className="flow-badge flow-badge-skip">⏭️ {acc.betSkipReason}</span>
                                      : acc.betSuccess ? <span className="flow-badge flow-badge-ok">✅{acc.barcode ? ` Barkod: ${acc.barcode}` : ''}</span>
                                      : <span className="flow-badge flow-badge-fail">❌{acc.betError ? ` ${acc.betError}` : ''}{acc.playErrorCode ? ` (${acc.playErrorCode})` : ''}</span>}
                                      {acc.odd && <span className="flow-acc-meta">Oran: {acc.odd} | {acc.amount}₺</span>}
                                    </div>
                                    {!acc.betSkipReason && (
                                      <div className="flow-step-body">
                                        {(acc.playRequest || acc.batches?.[0]?.playRequest) && (
                                          <>
                                            <div className="bl-section"><h4>Play Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.playRequest || acc.playRequest, null, 2)}</pre></div>
                                            <div className="bl-section"><h4>Play Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.playResponse || acc.playResponse, null, 2)}</pre></div>
                                          </>
                                        )}
                                        {(acc.validateRequest || acc.batches?.[0]?.validateRequest) && (
                                          <>
                                            <div className="bl-section"><h4>Validate Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateRequest || acc.validateRequest, null, 2)}</pre></div>
                                            <div className="bl-section"><h4>Validate Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateResponse || acc.validateResponse, null, 2)}</pre></div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Follower/Paralel Hesaplar */}
                            {parallelAccs.length > 0 && parallelAccs.some(a => !a.betSkipReason || a.playRequest || a.batches?.length) && (
                              <div className="flow-step">
                                <div className="flow-step-header">
                                  <span className="flow-step-num">{(goal.marketCheck || goal.marketCheckRequest || goal.acikMarketRequest) ? (initialGroupAccs.length > 0 ? '4' : '3') : (initialGroupAccs.length > 0 ? '3' : '2')}</span>
                                  <span className="flow-step-title">👥 Paralel Hesaplar ({parallelAccs.length})</span>
                                  {(() => {
                                    const played = parallelAccs.filter(a => !a.betSkipReason);
                                    const ok = played.filter(a => a.betSuccess).length;
                                    if (played.length === 0) return <span className="flow-badge flow-badge-skip">⏭️ Tümü atlandı</span>;
                                    return ok > 0
                                      ? <span className="flow-badge flow-badge-ok">✅ {ok}/{played.length}</span>
                                      : <span className="flow-badge flow-badge-fail">❌ 0/{played.length}</span>;
                                  })()}
                                </div>
                                {parallelAccs.map((acc, ai) => (
                                  <div key={ai} className="flow-sub-account">
                                    <div className="flow-sub-header">
                                      <strong>{acc.displayName || displayNameMap[acc.nesineUser] || acc.nesineUser}</strong>
                                      {acc.betSkipReason ? <span className="flow-badge flow-badge-skip">⏭️ {acc.betSkipReason}</span>
                                      : acc.betSuccess ? <span className="flow-badge flow-badge-ok">✅{acc.barcode ? ` Barkod: ${acc.barcode}` : ''}</span>
                                      : <span className="flow-badge flow-badge-fail">❌{acc.betError ? ` ${acc.betError}` : ''}{acc.playErrorCode ? ` (${acc.playErrorCode})` : ''}</span>}
                                      {acc.odd && <span className="flow-acc-meta">Oran: {acc.odd} | {acc.amount}₺</span>}
                                    </div>
                                    {!acc.betSkipReason && (
                                      <div className="flow-step-body">
                                        {(acc.playRequest || acc.batches?.[0]?.playRequest) && (
                                          <>
                                            <div className="bl-section"><h4>Play Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.playRequest || acc.playRequest, null, 2)}</pre></div>
                                            <div className="bl-section"><h4>Play Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.playResponse || acc.playResponse, null, 2)}</pre></div>
                                          </>
                                        )}
                                        {(acc.validateRequest || acc.batches?.[0]?.validateRequest) && (
                                          <>
                                            <div className="bl-section"><h4>Validate Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateRequest || acc.validateRequest, null, 2)}</pre></div>
                                            <div className="bl-section"><h4>Validate Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateResponse || acc.validateResponse, null, 2)}</pre></div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Bottom Pagination */}
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={(p) => { handlePageChange(p); }}
      />
    </div>
  );
}

export default BetLogs;
